import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Updated Dec 1 2024: All 6 payment products now supported

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#1f2937',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#9ca3af',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

const PRODUCTS = {
  'licensing-guide': { 
    amount: 79.00, 
    name: 'Licensed or Unlicensed: A 50-State Home Care Licensing Guide',
    description: 'Understand exactly what\'s required to open a home care agency in any U.S. state. This guide breaks down every state\'s licensing requirements, application fees, and department links — all in one organized resource.',
    features: [
      'Which states require a home care license',
      'Which states allow unlicensed non-medical agencies',
      'Certificate of Need (CON) states',
      'Direct links to each state\'s health department and application forms'
    ],
    downloadUrl: `${BACKEND_URL}/api/download/licensing-guide`
  },
  'referral-database': { 
    amount: 149.99, 
    name: 'Referral Partner Database',
    description: 'Access a curated list of over 300 verified healthcare, insurance, and workers\' compensation referral partners to help your agency get in-network and approved for referrals faster.',
    features: [
      '300+ verified healthcare contacts',
      'Insurance company referral partners',
      'Workers\' compensation connections',
      'Direct contact information included',
      'Designed for home care, home health, and medical service providers'
    ],
    downloadUrl: `${BACKEND_URL}/api/download/master-referral-list`
  },
  'power-session': {
    amount: 99.00,
    name: 'Pick My Brain Power Session',
    description: '30 minutes of laser-focused consulting on your specific challenge. Get unstuck fast with expert guidance.',
    features: [
      '30-minute one-on-one consultation',
      'Laser-focused on your specific challenge',
      'Expert guidance from successful agency owner',
      'Immediate actionable advice'
    ],
    downloadUrl: null,
    calendlyUrl: 'https://calendly.com/admin-abilityhealthsolutions/pick-my-brain'
  },
  'comprehensive-consultation': {
    amount: 2499.00,
    name: 'Comprehensive Consultation',
    description: 'Intense session covering every aspect of starting and running a successful home care agency.',
    features: [
      'In-depth agency setup guidance',
      'Complete operations overview',
      'Marketing and growth strategies',
      'Licensing and compliance guidance',
      'Systems and automation setup'
    ],
    downloadUrl: null,
    calendlyUrl: 'https://calendly.com/admin-abilityhealthsolutions/pick-my-brain'
  },
  'agency-build': {
    amount: 4999.00,
    name: 'Agency Build & Strategy Walkthrough',
    description: '8 hours of hands-on support to build your agency from the ground up with proven 7-figure systems.',
    features: [
      'Complete agency setup (8 hours)',
      'Marketing automation implementation',
      'Systems implementation',
      'Ongoing support included',
      'Proven 7-figure systems'
    ],
    downloadUrl: null,
    calendlyUrl: 'https://calendly.com/admin-abilityhealthsolutions/pick-my-brain'
  },
  'built-agency': {
    amount: 14999.00,
    name: 'Built For You Home Care Agency',
    description: 'Fully licensed, ready-to-operate home care agency built for you. Skip months of setup and start serving clients immediately.',
    features: [
      'Already licensed and ready to operate',
      'All legal documents included',
      'Start serving clients immediately',
      'Skip months of setup time',
      'Turnkey solution'
    ],
    downloadUrl: null,
    calendlyUrl: 'https://calendly.com/admin-abilityhealthsolutions/pick-my-brain'
  },
  'group-consultation': {
    amount: 399.00,
    name: 'Start My Home Care Agency',
    description: 'Join our exclusive group consultation on January 3rd, 2025 at 1:00 PM EST and learn everything you need to successfully launch your home care agency.',
    features: [
      'January 3rd, 2025 at 1:00 PM EST',
      'Business structure guidance',
      'NPI application assistance',
      'Insurance credentialing requirements',
      'Compliant Policies & Procedures examples',
      'Commercial insurance contracting tips',
      'VA community care provider approval',
      'Medicaid & Medicare insider secrets',
      'Staffing and system needs'
    ],
    downloadUrl: null,
    calendlyUrl: null
  }
};

function CheckoutForm({ product, productId, clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(product.amount);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setError('Please enter a promotion code');
      return;
    }

    try {
      // Validate Stripe promotion code
      const { data } = await axios.post(`${API}/validate-stripe-promo`, {
        promotion_code: couponCode,
        product_id: productId,
        amount: product.amount
      });

      if (data.valid) {
        setDiscount(data.discount);
        setFinalAmount(data.final_amount);
        setCouponApplied(true);
        setError(null);
      } else {
        setError(data.message || 'Invalid promotion code');
        setCouponApplied(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid promotion code');
      setCouponApplied(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!email || !name) {
      setError('Please provide your name and email');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm payment with any payment method (Klarna, Afterpay, Card, etc.)
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?product=${productId}`,
          payment_method_data: {
            billing_details: {
              name: name,
              email: email,
            },
          },
        },
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message);
        setProcessing(false);
      } else {
        if (result.paymentIntent.status === 'succeeded') {
          // If product has Calendly URL (consultation service), redirect to booking page
          if (product.calendlyUrl) {
            navigate('/booking-success', { 
              state: { 
                productName: product.name,
                calendlyUrl: product.calendlyUrl
              } 
            });
          } else {
            // For digital downloads, show success on same page
            setPaymentSuccess(true);
          }
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="text-center py-12">
        <div className="mb-6">
          <svg className="w-24 h-24 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-4">Payment Successful!</h2>
        <p className="text-xl text-gray-600 mb-8">
          Thank you for your purchase. Your payment has been processed.
        </p>

        {product.downloadUrl && (
          <div className="mb-8">
            <a
              href={product.downloadUrl}
              download
              className="inline-block bg-gradient-to-r from-blue-600 to-blue-700 text-white px-10 py-4 rounded-full font-bold hover:from-blue-700 hover:to-blue-800 transition-all text-lg uppercase tracking-wide shadow-xl"
            >
              Download Now →
            </a>
            <p className="text-gray-500 text-sm mt-4">
              A download link has also been sent to {email}
            </p>
          </div>
        )}

        <Link
          to="/"
          className="inline-block text-blue-600 hover:text-blue-700 font-semibold"
        >
          ← Return to Home
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name *
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="John Doe"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="john@example.com"
          />
        </div>
      </div>

      {/* Promotion Code Section */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Have a Promotion Code?
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
            disabled={couponApplied}
            placeholder="Enter promotion code"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={handleApplyCoupon}
            disabled={couponApplied || !couponCode.trim()}
            className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {couponApplied ? '✓ Applied' : 'Apply'}
          </button>
        </div>
        {couponApplied && (
          <div className="mt-3 p-3 bg-green-50 border border-green-300 rounded-lg">
            <p className="text-green-800 font-semibold">
              🎉 Promotion code applied! You save ${discount.toFixed(2)}
            </p>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Method *
        </label>
        <div className="border border-gray-300 rounded-lg p-4 bg-white">
          <PaymentElement />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          💳 Card • Klarna • Afterpay • Affirm • Cash App accepted
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 text-red-800">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="flex items-center text-sm text-gray-600">
        <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
        Secure payment powered by Stripe
      </div>

      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-blue-800 transition-all text-xl uppercase tracking-wide shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {processing ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
        ) : (
          <>
            {couponApplied ? (
              <span>
                Pay ${finalAmount.toFixed(2)} <span className="line-through opacity-70 text-sm ml-2">${product.amount.toFixed(2)}</span>
              </span>
            ) : (
              `Pay $${product.amount.toFixed(2)}`
            )}
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 text-center">
        By completing this purchase, you agree to our <Link to="/terms" className="text-blue-600 hover:underline">Terms & Conditions</Link>
      </p>
    </form>
  );
}

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const productId = searchParams.get('product');
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Create payment intent on component mount to get client secret
    const createIntent = async () => {
      try {
        const { data } = await axios.post(`${API}/create-payment-intent`, {
          product_id: productId,
          customer_email: 'temp@temp.com', // Temporary, will be updated
          customer_name: 'Temporary'
        });
        setClientSecret(data.client_secret);
        setLoading(false);
      } catch (error) {
        console.error('Error creating payment intent:', error);
        setLoading(false);
      }
    };

    if (productId && PRODUCTS[productId]) {
      createIntent();
    }
  }, [productId]);

  if (!productId || !PRODUCTS[productId]) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/" className="text-blue-600 hover:underline">Return to Home</Link>
        </div>
      </div>
    );
  }

  const product = PRODUCTS[productId];

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-semibold flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Details */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-blue-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {product.name}
            </h1>
            <div className="text-5xl font-black text-blue-600 mb-6">
              ${product.amount}
            </div>
            
            <p className="text-gray-700 text-lg mb-6">
              {product.description}
            </p>

            <div className="bg-blue-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">What's Included:</h3>
              <ul className="space-y-3">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {product.downloadUrl && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-semibold">
                  ✓ Instant Digital Download
                </p>
                <p className="text-green-700 text-sm mt-1">
                  Access your purchase immediately after payment
                </p>
              </div>
            )}
          </div>

          {/* Payment Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Complete Your Purchase
            </h2>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">Loading payment form...</p>
              </div>
            ) : clientSecret ? (
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm product={product} productId={productId} clientSecret={clientSecret} />
              </Elements>
            ) : (
              <div className="text-center py-8 text-red-600">
                Error loading payment form. Please try again.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
